import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@primereact/ui/button';

import { API_CHAT_TOPIC, API_GUARDIAN_REPORT, API_GUARDIAN_SQUELCH, API_GUARDIAN_UNSQUELCH, VIEW_MODERATION } from './EsConstants';
import { esPost, esPut } from './EsFetch';
import { EsDialog, EsTextarea } from './EsPrime';
import { useEsGuardianFlag } from './EsFlags';
import { useEsStrings } from './EsStrings';
import { useAuth } from '../views/parent/EsAuth';
import './EsGuardian.css';

/**
 * Guardian, the browser's half (esback's guardian package): the squelch and unsquelch
 * control a moderator sees inline where somebody's words show, the placeholder everybody
 * else reads in their place, and the notice a banned account sees on every page.
 *
 * A moderator is an ADMIN or an ALPHA with rolloutEsGuardian on -- the same rule
 * EsGuardianService keeps, mirrored here only to hide what the server would refuse.
 */
export const MODERATOR_ROLES = [ 'ADMIN', 'ALPHA' ];

export function useEsModerator() {
    const account = useAuth()?.account;
    const aOn = useEsGuardianFlag();
    return aOn && MODERATOR_ROLES.includes( account?.role );
}

/**
 * What a squelched text reads as to anybody but a moderator; gm says the table's GM hid it, and
 * held that the filter holds it for a moderator to read (esback's V192), which no person has yet.
 */
export function EsGuardianPlaceholder( { kind, id, className = '', gm = false, held = false } ) {
    const t = useEsStrings();
    return (
        <span className={ 'esMuted italic ' + className } data-testid={ 'es-guardian-hidden-' + kind + '-' + id }>
            { held ? t( 'guardian-held', 'Held for review.' )
                : gm ? t( 'guardian-gm-squelched', 'Hidden by the GM.' ) : t( 'guardian-squelched', 'Hidden by a moderator.' ) }
        </span>
    );
}

/**
 * The mark beside held words their reader may see -- their author, or a moderator: the filter
 * holds them for a moderator, and nobody else reads them until one releases them.
 */
export function EsGuardianHeldMark( { kind, id, className = '' } ) {
    const t = useEsStrings();
    return (
        <span className={ 'guardianMark guardianMarkHeld ' + className } data-testid={ 'es-guardian-held-' + kind + '-' + id }>
            <i className="pi pi-clock" aria-hidden="true" /> { t( 'guardian-held-mark', 'Held for review' ) }
        </span>
    );
}

/**
 * The mark beside squelched words their reader still sees -- a moderator, or the GM who hid
 * them (T132): without it the line read as any other, and nothing said Unsquelch would put it
 * back for everybody. gm says the squelch standing is the table GM's.
 */
export function EsGuardianHiddenMark( { kind, id, className = '', gm = false } ) {
    const t = useEsStrings();
    return (
        <span className={ 'guardianMark guardianMarkHidden ' + className } data-testid={ 'es-guardian-hidden-badge-' + kind + '-' + id }>
            <i className="pi pi-eye-slash" aria-hidden="true" />
            { gm ? t( 'guardian-gm-hidden-mark', 'Hidden by the GM' ) : t( 'guardian-hidden-mark', 'Hidden by a moderator' ) }
        </span>
    );
}

/**
 * Which mark squelched words carry where their reader sees them: held when the filter holds them
 * for a moderator, hidden when a person squelched them, none when they show to everybody.
 * shown says this reader has the words; without them the placeholder says it all.
 */
export function esGuardianState( { squelched, held, shown } ) {
    if ( !shown ) return null;
    if ( held ) return 'held';
    return squelched ? 'hidden' : null;
}

/** The row class that tints and dims marked words (EsGuardian.css), or '' for unmarked ones. */
export function esGuardianLineClass( pState ) {
    if ( pState === 'held' ) return ' guardianLineHeld';
    return pState === 'hidden' ? ' guardianLineHidden' : '';
}

/** The badge for a state from esGuardianState, or nothing. */
export function EsGuardianMark( { state, kind, id, gm = false, className = '' } ) {
    if ( state === 'held' ) return <EsGuardianHeldMark kind={ kind } id={ id } className={ className } />;
    if ( state === 'hidden' ) return <EsGuardianHiddenMark kind={ kind } id={ id } gm={ gm } className={ className } />;
    return null;
}

/** What a report or a squelch is of (EsSquelchKind, or ACCOUNT for a profile), in the words esback mails it by. */
export function esGuardianKindName( pKind, t ) {
    switch ( pKind ) {
        case 'CHAT_POST': return t( 'server-guardian-kind-chat-post', 'a Commons post' );
        case 'CHAT_LINE': return t( 'server-guardian-kind-chat-line', 'a table chat line' );
        case 'COMMENT': return t( 'server-guardian-kind-comment', 'a codex comment' );
        case 'ENTRY': return t( 'server-guardian-kind-entry', 'a codex entry' );
        case 'SECTION': return t( 'server-guardian-kind-section', 'a codex section' );
        case 'LFG_POST': return t( 'server-guardian-kind-lfg-post', 'a Commons table post' );
        case 'LFG_APPLICATION': return t( 'server-guardian-kind-lfg-application', 'a seat application' );
        case 'LFG_REPORT': return t( 'server-guardian-kind-lfg-report', 'a report' );
        case 'ACCOUNT': return t( 'server-guardian-kind-account', 'a profile' );
        default: return t( 'guardian-kind-other', 'something' );
    }
}

/** A Comprehend label (EsGuardianThresholds) in the words esback mails it by. */
export function esGuardianLabelName( pLabel, t ) {
    switch ( pLabel ) {
        case 'HATE_SPEECH': return t( 'server-guardian-label-hate-speech', 'hate speech' );
        case 'HARASSMENT_OR_ABUSE': return t( 'server-guardian-label-harassment-or-abuse', 'harassment or abuse' );
        case 'SEXUAL': return t( 'server-guardian-label-sexual', 'sexual content' );
        case 'VIOLENCE_OR_THREAT': return t( 'server-guardian-label-violence-or-threat', 'violence or threats' );
        case 'GRAPHIC': return t( 'server-guardian-label-graphic', 'graphic content' );
        case 'INSULT': return t( 'server-guardian-label-insult', 'insults' );
        case 'PROFANITY': return t( 'server-guardian-label-profanity', 'profanity' );
        default: return String( pLabel ?? '' ).toLowerCase().replace( /_/g, ' ' );
    }
}

/**
 * A moderator's squelch asks which rule the words break: esback refuses one without, and the
 * author's mail says it ("broke the community rules: ..."). onDone has esback's answer. labels,
 * for words the filter holds, are what scored: the reason may be left blank, and esback then
 * mails the labels by name in the author's language.
 */
export function EsGuardianSquelchDialog( { kind, id, onDone, onClose, labels = null } ) {
    const t = useEsStrings();
    const [ why, setWhy ] = useState( '' );
    const [ busy, setBusy ] = useState( false );
    const [ problem, setProblem ] = useState( '' );

    const aOptional = labels != null;
    const aReady = aOptional || Boolean( why.trim() );

    const send = ( pEvent ) => {
        pEvent.preventDefault();
        if ( !aReady || busy ) return;
        setBusy( true );
        setProblem( '' );
        esPost( API_GUARDIAN_SQUELCH, { kind, id, why: why.trim() } )
            .then( ( pView ) => onDone( pView ) )
            .catch( ( pError ) => setProblem( pError?.message || t( 'guardian-squelch-failed', 'The squelch did not go. Try again.' ) ) )
            .finally( () => setBusy( false ) );
    };

    return (
        <EsDialog open onOpenChange={ ( pOpen ) => { if ( !pOpen ) onClose(); } }
                  title={ t( 'guardian-squelch', 'Squelch' ) } data-testid="es-guardian-squelch-dialog">
            <form className="flex flex-col gap-3" onSubmit={ send }>
                <label className="esLabel" htmlFor="es-guardian-squelch-why">{ t( 'guardian-squelch-why', 'Which rule does this break?' ) }</label>
                <EsTextarea id="es-guardian-squelch-why" data-testid="es-guardian-squelch-why" value={ why }
                            placeholder={ aOptional ? labels : undefined }
                            onChange={ ( pEvent ) => setWhy( pEvent.target.value ) } />
                { aOptional &&
                    <p className="esAside" data-testid="es-guardian-squelch-labels">
                        { t( 'guardian-squelch-labels', 'Leave it blank to give what the filter found: {labels}.', { labels } ) }
                    </p> }
                { problem && <p className="chatProblem" role="alert" data-testid="es-guardian-squelch-problem">{ problem }</p> }
                <Button type="submit" size="small" data-testid="es-guardian-squelch-send" disabled={ busy || !aReady }>
                    { t( 'guardian-squelch', 'Squelch' ) }
                </Button>
            </form>
        </EsDialog>
    );
}

/**
 * One icon button: squelch when the words show, unsquelch when they are hidden. Calls
 * esback itself and tells its parent the new state through onChanged; a parent that polls
 * sees the same answer on the next read. gm is esback's word that this reader, a table's GM,
 * may hide or put back this line (a player's, at their table): shown to them as to a moderator.
 * A moderator's squelch asks why first (EsGuardianSquelchDialog); a GM's hides at once.
 */
export function EsGuardianControl( { kind, id, squelched, onChanged, gm = false } ) {
    const t = useEsStrings();
    const moderator = useEsModerator();
    const [ busy, setBusy ] = useState( false );
    const [ asking, setAsking ] = useState( false );
    const [ hidden, setHidden ] = useState( Boolean( squelched ) );

    useEffect( () => { setHidden( Boolean( squelched ) ); }, [ squelched ] );

    if ( !( moderator || gm ) || id == null ) return null;

    const aLabel = hidden ? t( 'guardian-unsquelch', 'Unsquelch' ) : t( 'guardian-squelch', 'Squelch' );
    const aTip = hidden ? t( 'guardian-unsquelch-tip', 'Unsquelch: show these words to everybody again' ) : aLabel;
    const aTestId = 'es-guardian-' + ( hidden ? 'unsquelch' : 'squelch' ) + '-' + kind + '-' + id;

    const changed = ( pView ) => {
        const aNow = Boolean( pView?.squelchedStamp );
        setHidden( aNow );
        if ( onChanged ) onChanged( aNow, pView );
    };

    const press = () => {
        if ( !hidden && moderator ) {
            setAsking( true );
            return;
        }
        setBusy( true );
        esPost( hidden ? API_GUARDIAN_UNSQUELCH : API_GUARDIAN_SQUELCH, { kind, id } )
            .then( changed )
            .catch( () => { /* The next read says what stands. */ } )
            .finally( () => setBusy( false ) );
    };

    return (
        <>
            <button type="button" className="chatIcon guardianControl" aria-label={ aLabel } data-tip={ aTip }
                    data-testid={ aTestId } disabled={ busy } onClick={ press }>
                <i className={ 'pi ' + ( hidden ? 'pi-eye' : 'pi-ban' ) } aria-hidden="true" />
                { hidden && <span className="guardianControlText" aria-hidden="true">{ aLabel }</span> }
            </button>
            { asking &&
                <EsGuardianSquelchDialog kind={ kind } id={ id } onClose={ () => setAsking( false ) }
                                         onDone={ ( pView ) => { setAsking( false ); changed( pView ); } } /> }
        </>
    );
}

/** The banner a banned account sees under the nav: why every other request is refused, and until when. */
export function EsGuardianBanNotice() {
    const t = useEsStrings();
    const account = useAuth()?.account;
    if ( !account?.bannedUntil ) return null;

    const aUntil = new Date( account.bannedUntil );
    const aShown = Number.isNaN( aUntil.getTime() ) ? String( account.bannedUntil )
        : aUntil.toLocaleString( undefined, { dateStyle: 'medium', timeStyle: 'short' } );

    return (
        <div role="alert" data-testid="es-guardian-banned"
             className="fixed inset-x-4 top-24 z-50 mx-auto max-w-[640px] rounded-md border border-field-edge bg-surface px-6 py-4 text-base text-body shadow-one">
            { t( 'guardian-banned',
                'Your account is suspended until {until} because something you wrote broke the community rules.',
                { until: aShown } ) }
        </div>
    );
}

/**
 * Report, for any signed-in account with rolloutEsGuardian on: a dialog with a reason, one per
 * account per item. The refusal for a second press is esback's sentence, shown in the dialog.
 * Without the flag EsFlagFilter answers the report "Feature not available", so there is no
 * button to press (T89, 2026-10-29). authorId, when given, hides it from the words' own author
 * (T108), whom esback refuses for a Commons post.
 */
export function EsGuardianReport( { kind, id, authorId = null } ) {
    const t = useEsStrings();
    const account = useAuth()?.account;
    const aOn = useEsGuardianFlag();
    const [ open, setOpen ] = useState( false );
    const [ why, setWhy ] = useState( '' );
    const [ sent, setSent ] = useState( false );
    const [ busy, setBusy ] = useState( false );
    const [ problem, setProblem ] = useState( '' );

    if ( !account || !aOn || id == null || ( authorId != null && Number( authorId ) === Number( account.accountId ) ) ) return null;

    const aLabel = t( 'guardian-report', 'Report' );
    const close = () => { setOpen( false ); setWhy( '' ); setSent( false ); setProblem( '' ); };

    const send = ( pEvent ) => {
        pEvent.preventDefault();
        if ( !why.trim() || busy ) return;
        setBusy( true );
        setProblem( '' );
        esPost( API_GUARDIAN_REPORT, { kind, id, why: why.trim() } )
            .then( () => setSent( true ) )
            .catch( ( pError ) => setProblem( pError?.message || t( 'guardian-report-failed', 'The report did not go. Try again.' ) ) )
            .finally( () => setBusy( false ) );
    };

    return (
        <>
            <button type="button" className="chatIcon guardianReport" aria-label={ aLabel } data-tip={ aLabel }
                    data-testid={ 'es-guardian-report-' + kind + '-' + id } onClick={ () => setOpen( true ) }>
                <i className="pi pi-flag" aria-hidden="true" />
            </button>
            { open &&
                <EsDialog open onOpenChange={ ( pOpen ) => { if ( !pOpen ) close(); } }
                          title={ aLabel } data-testid="es-guardian-report-dialog">
                    { sent
                        ? <p className="esAside" data-testid="es-guardian-report-sent">
                            { t( 'guardian-report-sent', 'Thanks. A moderator will look.' ) }
                        </p>
                        : <form className="flex flex-col gap-3" onSubmit={ send }>
                            <label className="esLabel" htmlFor="es-guardian-report-why">{ t( 'guardian-report-why', 'What is wrong?' ) }</label>
                            <EsTextarea id="es-guardian-report-why" data-testid="es-guardian-report-why" value={ why }
                                        onChange={ ( pEvent ) => setWhy( pEvent.target.value ) } />
                            { problem && <p className="chatProblem" role="alert" data-testid="es-guardian-report-problem">{ problem }</p> }
                            <Button type="submit" size="small" data-testid="es-guardian-report-send" disabled={ busy || !why.trim() }>
                                { t( 'guardian-report-send', 'Send report' ) }
                            </Button>
                        </form> }
                </EsDialog> }
        </>
    );
}

/** An admin's slow mode on a topic: seconds between one account's posts, 0 for none. Saved on blur or Enter. */
export function EsGuardianSlowMode( { topic, onSaved } ) {
    const t = useEsStrings();
    const admin = useAuth()?.admin;
    const aOn = useEsGuardianFlag();
    const [ seconds, setSeconds ] = useState( topic?.slowSeconds ?? 0 );
    const [ busy, setBusy ] = useState( false );

    useEffect( () => { setSeconds( topic?.slowSeconds ?? 0 ); }, [ topic?.slowSeconds ] );

    if ( !admin || !aOn || !topic?.key ) return null;

    const save = () => {
        const aSeconds = Math.max( 0, Math.min( 3600, Number( seconds ) || 0 ) );
        if ( aSeconds === ( topic.slowSeconds ?? 0 ) ) return;
        setBusy( true );
        esPut( API_CHAT_TOPIC + '/' + topic.key, { title: topic.title, group: topic.group?.group, slowSeconds: aSeconds } )
            .then( ( pTopic ) => { if ( onSaved ) onSaved( pTopic ); } )
            .catch( () => setSeconds( topic.slowSeconds ?? 0 ) )
            .finally( () => setBusy( false ) );
    };

    const aLabel = t( 'guardian-slow', 'Slow mode (seconds)' );
    return (
        <label className="esLabel flex items-center gap-1" data-testid="es-guardian-slow">
            <i className="pi pi-clock" aria-hidden="true" data-tip={ aLabel } />
            <input type="number" min="0" max="3600" className="w-16 rounded border border-field-edge bg-field px-1 text-body"
                   aria-label={ aLabel } data-testid="es-guardian-slow-seconds" value={ seconds } disabled={ busy }
                   onChange={ ( pEvent ) => setSeconds( pEvent.target.value ) } onBlur={ save }
                   onKeyDown={ ( pEvent ) => { if ( pEvent.key === 'Enter' ) { pEvent.preventDefault(); save(); } } } />
        </label>
    );
}

/** The way to the moderation view, for a moderator. */
export function EsGuardianModerationLink() {
    const t = useEsStrings();
    const moderator = useEsModerator();
    if ( !moderator ) return null;
    return (
        <Link to={ VIEW_MODERATION } className="esLabel" data-testid="es-guardian-moderation-link">
            <i className="pi pi-shield" aria-hidden="true" /> { t( 'guardian-moderation', 'Moderation' ) }
        </Link>
    );
}
